/**
 * ArticleFolderService
 *
 * Manages ArticleFolder entities for organizing article drafts into folders.
 *
 * Structure:
 * - projects/{projectId}/articleFolders/{folderId}
 *
 * Clone of the proven folder-service family (OnboardingFolderService,
 * ABTestFolderService, WebABTestFolderService).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  FirestoreDataConverter,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import {
  ArticleFolder,
  articleFolderConverter,
} from '../converters/articleFolderConverter';

/**
 * Input type for creating a new folder
 */
export interface CreateArticleFolderInput {
  name: string;
  parentFolderId?: string | null;
}

/**
 * Input type for updating a folder
 */
export interface UpdateArticleFolderInput {
  name?: string;
}

/**
 * ArticleFolderService
 *
 * Provides CRUD operations for article folders
 */
export class ArticleFolderService {
  private readonly collectionName = 'articleFolders';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<ArticleFolder>;

  constructor(converter: FirestoreDataConverter<ArticleFolder> = articleFolderConverter) {
    this.converter = converter;
  }

  /**
   * Create a new folder
   *
   * @param projectId - Project ID
   * @param data - Folder data (name, optional parentFolderId)
   * @returns Created folder with generated id
   */
  async create(projectId: string, data: CreateArticleFolderInput): Promise<ArticleFolder> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      );
      const docRef = doc(collectionRef).withConverter(this.converter);

      const now = new Date().toISOString();
      const folderData: ArticleFolder = {
        id: docRef.id,
        projectId,
        name: data.name,
        parentFolderId: data.parentFolderId ?? null,
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(docRef, folderData);
      return folderData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ArticleFolderService.create');
      throw error;
    }
  }

  /**
   * Get a folder by ID
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID
   * @returns Folder or null if not found
   */
  async get(projectId: string, folderId: string): Promise<ArticleFolder | null> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      ).withConverter(this.converter);

      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { ...docSnap.data(), id: docSnap.id };
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ArticleFolderService.get');
      throw error;
    }
  }

  /**
   * List all folders for a project
   *
   * @param projectId - Project ID
   * @returns Array of folders ordered by name
   */
  async list(projectId: string): Promise<ArticleFolder[]> {
    try {
      const collectionRef = collection(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`
      ).withConverter(this.converter);

      const q = query(collectionRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ArticleFolderService.list');
      throw error;
    }
  }

  /**
   * Update a folder
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID
   * @param updates - Fields to update
   */
  async update(projectId: string, folderId: string, updates: UpdateArticleFolderInput): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      ).withConverter(this.converter);

      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await setDoc(docRef, updateData as ArticleFolder, { merge: true });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ArticleFolderService.update');
      throw error;
    }
  }

  /**
   * Delete a folder
   *
   * NOTE: This does not cascade delete articles in the folder.
   * The UI blocks deleting a non-empty folder (checks contents in memory
   * before calling this).
   *
   * @param projectId - Project ID
   * @param folderId - Folder ID to delete
   */
  async delete(projectId: string, folderId: string): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.collectionName}`,
        folderId
      );

      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ArticleFolderService.delete');
      throw error;
    }
  }
}
